import './DualBar.css';
import { t } from '../copy/strings';
import { fmtPct } from '../format/numbers';
import { type Party, partyClass } from '../theme/parties';

export interface DualBarProps {
  /** Fração dos válidos de cada lado (`PlaceView.shares`); [0, 0] = sem dados. */
  shares: readonly [number, number];
  parties: readonly [Party, Party];
  /** Nomes de urna para o `aria-label`; sem eles, a sigla do partido. */
  names?: readonly [string, string];
  /** Disputa definida: a marca encolhe para 4/4 px na variante `hero`. */
  decided?: boolean;
  variant?: 'hero' | 'thin';
  /** `aria-label` do dono (ex.: `duel.aria`); sem ele, `bar.dual.aria`. */
  label?: string;
}

function frac(v: number): number {
  if (!Number.isFinite(v) || v <= 0) return 0;
  return Math.round(Math.min(v, 1) * 1e6) / 1e6;
}

/**
 * Barra bicolor com marca de 50% (§3.8 `DualBar`). O lado A é sempre o de número menor (regra 2 de neutralidade):
 * se o dono passar os lados invertidos, o componente os reordena (partes, fatias e nomes juntos).
 */
export function DualBar({ shares, parties, names, decided = false, variant = 'hero', label }: DualBarProps) {
  const [p0, p1] = parties;
  const swap = p0.number > 0 && p1.number > 0 && p0.number > p1.number;
  const pA = swap ? p1 : p0;
  const pB = swap ? p0 : p1;
  const sA = frac(swap ? shares[1] : shares[0]);
  const sB = frac(swap ? shares[0] : shares[1]);
  const nA = (swap ? names?.[1] : names?.[0]) ?? pA.display;
  const nB = (swap ? names?.[0] : names?.[1]) ?? pB.display;
  const empty = sA === 0 && sB === 0;
  const dash = t('common.dash');
  const aria =
    label ||
    t('bar.dual.aria', {
      A: nA,
      pA: empty ? dash : fmtPct(sA, 2),
      B: nB,
      pB: empty ? dash : fmtPct(sB, 2),
    });
  const state = decided ? 'decided' : empty ? 'empty' : 'counting';
  return (
    <span
      class={`dual-bar dual-bar--${variant}`}
      data-testid="dual-bar"
      data-state={state}
      data-dataviz=""
      role="img"
      aria-label={aria}
    >
      <span class="dual-bar__track">
        <i class={`dual-bar__seg dual-bar__seg--a ${partyClass(pA)}`} style={{ '--w': String(sA) }} />
        <i class={`dual-bar__seg dual-bar__seg--b ${partyClass(pB)}`} style={{ '--w': String(sB) }} />
      </span>
      <span class="dual-bar__fifty" />
    </span>
  );
}
